import React from 'react';
import {
  ResponsiveContainer,
  AreaChart as RechartsAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Brush
} from 'recharts';
import { format } from 'date-fns';
import { Box, Typography, Paper } from '@mui/material';

const AreaChart = ({
  data = [],
  dataKey = 'value',
  xAxisKey = 'label',
  valueFormatter,
  margin = { top: 20, right: 30, left: 20, bottom: 20 },
  showTooltip = true,
  showGrid = true,
  gradientId = 'stunning-gradient',
  strokeColor = '#ffffff',
  strokeWidth = 2,
  showActiveDot = true,
  yAxisFormatter = (value) => value,
  xAxisFormatter = (value) => value,
  tooltipFormatter = (value, name) => [value, name],
  tooltipLabelFormatter = (label) => label,
  enableBrush = true,
  brushHeight = 28
}) => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsAreaChart data={data} margin={margin}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#dc2626" stopOpacity={0.4} />
            <stop offset="20%" stopColor="#f87171" stopOpacity={0.5} />
            <stop offset="40%" stopColor="#ea580c" stopOpacity={0.6} />
            <stop offset="60%" stopColor="#fb923c" stopOpacity={0.7} />
            <stop offset="80%" stopColor="#10b981" stopOpacity={0.8} />
            <stop offset="100%" stopColor="#065f46" stopOpacity={0.9} />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {showGrid && (
          <CartesianGrid 
            strokeDasharray="2 4" 
            stroke="hsl(var(--muted-foreground))" 
            strokeOpacity={0.15} 
            horizontal={true}
            vertical={false}
          />
        )}
        
        <XAxis 
          dataKey={xAxisKey} 
          tickFormatter={xAxisFormatter}
          axisLine={false}
          tickLine={false}
          tick={{ 
            fill: 'hsl(var(--muted-foreground))', 
            fontSize: 11,
            fontWeight: 500
          }}
          dy={10}
        />
        
        <YAxis 
          axisLine={false}
          tickLine={false}
          tick={{ 
            fill: 'hsl(var(--muted-foreground))', 
            fontSize: 11,
            fontWeight: 500
          }}
          tickFormatter={yAxisFormatter}
          domain={[0, 'dataMax + 5']}
        />
        
        {enableBrush && (
          <Brush
            dataKey={xAxisKey}
            height={brushHeight}
            travellerWidth={10}
            stroke="#94a3b8"
            fill="#f1f5f9"
          />
        )}

        {showTooltip && (
          <Tooltip
            content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                const value = payload[0].value;
                return (
                  <Paper
                    elevation={8}
                    sx={{
                      bgcolor: 'rgba(15, 23, 42, 0.95)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 1,
                      p: 2,
                      minWidth: 200
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        mb: 0.5,
                        display: 'block'
                      }}
                    >
                      {tooltipLabelFormatter(label)}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'white',
                        fontSize: '1.125rem',
                        fontWeight: 'bold'
                      }}
                    >
                      {tooltipFormatter(value, payload[0].name)}
                    </Typography>
                  </Paper>
                );
              }
              return null;
            }}
          />
        )}
        
        <Area
          type="natural"
          dataKey={dataKey}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          fill={`url(#${gradientId})`}
          dot={false}
          activeDot={showActiveDot ? { 
            r: 10, 
            stroke: '#ffffff', 
            strokeWidth: 4, 
            fill: '#8b5cf6',
            filter: 'drop-shadow(0 4px 16px rgba(139,92,246,0.6))',
            style: { cursor: 'pointer' }
          } : false}
        />
      </RechartsAreaChart>
    </ResponsiveContainer>
  );
};

export default AreaChart;
